import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Palette, Wand2, Lightbulb, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/api";

export default function OccasionsPage() {
  const navigate = useNavigate();
  const [occasions, setOccasions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/occasions");
        setOccasions(data.occasions || []);
      } catch (e) { toast.error("Couldn't load occasions. Please refresh."); }
    })();
  }, []);

  const pick = async (occ) => {
    setSelected(occ);
    setRec(null);
    setLoading(true);
    try {
      const { data } = await api.post("/recommend/occasion", { occasion: occ.id });
      setRec(data);
    } catch (e) {
      toast.error("Couldn't get a recommendation. Please try again.");
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-8" data-testid="occasions-page">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <span className="inline-block text-xs tracking-widest uppercase text-primary font-semibold mb-2">AI Stylist</span>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-foreground">What's the occasion?</h1>
          <p className="text-muted-foreground mt-2">Pick a moment and get a personalized nail look — style, colors, shape & finish.</p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {occasions.map((occ, i) => (
            <motion.button
              key={occ.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              whileHover={{ y: -4 }}
              onClick={() => pick(occ)}
              className="group relative rounded-3xl overflow-hidden shadow-soft aspect-[3/4] text-left"
              data-testid={`occasion-${occ.id}`}
            >
              <img src={occ.image} alt={occ.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <span className="text-2xl">{occ.icon}</span>
                <p className="text-white font-semibold text-sm mt-1">{occ.label}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            data-testid="occasion-modal"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-3xl max-w-lg w-full max-h-[92vh] overflow-y-auto shadow-2xl relative"
            >
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 text-foreground" data-testid="close-occasion-btn"><X className="w-5 h-5" /></button>
              <div className="relative h-48">
                <img src={selected.image} alt={selected.label} className="w-full h-full object-cover rounded-t-3xl" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-t-3xl" />
                <div className="absolute bottom-3 left-4"><span className="text-2xl">{selected.icon}</span><h2 className="font-playfair text-2xl font-bold text-white">{selected.label} nails</h2></div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="py-10 text-center"><Wand2 className="w-8 h-8 text-primary mx-auto animate-pulse" /><p className="text-muted-foreground mt-3 analyzing-text">Styling your look...</p></div>
                ) : rec ? (
                  <div className="space-y-4">
                    <p className="text-primary font-semibold">{rec.vibe}</p>
                    <p className="text-foreground/80">{rec.description}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-secondary/40 rounded-2xl p-3"><p className="text-muted-foreground text-xs">Shape</p><p className="font-medium text-foreground">{rec.shape}</p></div>
                      <div className="bg-secondary/40 rounded-2xl p-3"><p className="text-muted-foreground text-xs">Finish</p><p className="font-medium text-foreground">{rec.finish}</p></div>
                    </div>
                    <div>
                      <p className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2"><Palette className="w-4 h-4 text-primary" />Colors</p>
                      <div className="flex flex-wrap gap-2">{(rec.colors || []).map((c, i) => <span key={i} className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">{c}</span>)}</div>
                    </div>
                    <div>
                      <p className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2"><Sparkles className="w-4 h-4 text-primary" />The look</p>
                      <p className="text-foreground/80 text-sm">{rec.style}</p>
                    </div>
                    <div>
                      <p className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2"><Lightbulb className="w-4 h-4 text-amber-500" />Stylist tips</p>
                      <ul className="space-y-1 text-sm text-foreground/80">{(rec.tips || []).map((t, i) => <li key={i}>• {t}</li>)}</ul>
                    </div>
                    <Button onClick={() => navigate("/")} className="w-full rounded-full py-6 bg-primary hover:bg-primary/90" data-testid="try-look-btn">
                      Try this look & rate it <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
